/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box } from "@mui/material"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { parseISO, format } from "date-fns"
import { MyInput } from "../../../components/input/MyInput"
import { MyDatePicker } from "../../../components/input/MyDatePicker"
import { MyTimePicker } from "../../../components/input/MyTimePicker"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    text-align: center;
    max-width: 500px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2vw;
    width: 100%;
    
    ${mobileCss(`
        gap: 1.2rem;
    `)}
`)

const InputRowStyle = withMyTheme((theme) => css`
    display: flex;
    gap: 2vw;
    width: 100%;
    
    ${mobileCss(`
        flex-direction: column;
        gap: 1rem;
    `)}
`)

const DateTimeRowStyle = withMyTheme((theme) => css`
    display: flex;
    gap: 2vw;
    width: 100%;
    
    ${mobileCss(`
        flex-direction: column;
        gap: 1rem;
    `)}
`)

interface BasicInfoStepProps {
    onStart?: () => void
}

export const BasicInfoStep = ({ }: BasicInfoStepProps) => {
    const { t } = useTranslation()
    const { formData, updateBasicInfo } = useQuestionnaireForm()

    const handleCoupleNameChange = (field: 'coupleName1' | 'coupleName2') => (
        value: string
    ) => {
        updateBasicInfo({ [field]: value })
    }

    const handleDateChange = (date: Date | null) => {
        if (date) {
            try {
                const formattedDate = format(date, 'yyyy-MM-dd')
                updateBasicInfo({ weddingDate: formattedDate })
            } catch (error) {
                console.error('Error formatting date:', error)
            }
        } else {
            updateBasicInfo({ weddingDate: '' })
        }
    }

    const handleTimeChange = (time: Date | null) => {
        if (time) {
            try {
                const formattedTime = format(time, 'HH:mm')
                updateBasicInfo({ weddingTime: formattedTime })
            } catch (error) {
                console.error('Error formatting time:', error)
            }
        } else {
            updateBasicInfo({ weddingTime: '' })
        }
    }

    // Parse date and time for pickers
    const selectedDate = formData.basicInfo.weddingDate
        ? parseISO(formData.basicInfo.weddingDate)
        : null

    const selectedTime = formData.basicInfo.weddingTime
        ? parseISO(`2000-01-01T${formData.basicInfo.weddingTime}:00`)
        : null

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.basicInfo.title')}
            </Typography>

            <Box css={FormContainerStyle}>
                {/* Couple Names Row */}
                <Box css={InputRowStyle}>
                    <MyInput
                        label={t('questionnaire.basicInfo.coupleName1')}
                        placeholder={t('questionnaire.basicInfo.placeholders.coupleName1')}
                        value={formData.basicInfo.coupleName1}
                        onChange={handleCoupleNameChange('coupleName1')}
                    />
                    <MyInput
                        label={t('questionnaire.basicInfo.coupleName2')}
                        placeholder={t('questionnaire.basicInfo.placeholders.coupleName2')}
                        value={formData.basicInfo.coupleName2}
                        onChange={handleCoupleNameChange('coupleName2')}
                    />
                </Box>

                {/* Date and Time Row */}
                <Box css={DateTimeRowStyle}>
                    <MyDatePicker
                        label={t('questionnaire.basicInfo.weddingDate')}
                        value={selectedDate}
                        onChange={handleDateChange}
                        minDate={new Date()}
                    />
                    <MyTimePicker
                        label={t('questionnaire.basicInfo.weddingTime')}
                        value={selectedTime}
                        onChange={handleTimeChange}
                    />
                </Box>
            </Box>
        </div>
    )
}